import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import SharedProjectPage from "./pages/SharedProjectPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import DashboardPage from "./pages/DashboardPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import PeoplePage from "./pages/PeoplePage";
import SpecterPage from "./pages/SpecterPage";
import CalendarPage from "./pages/CalendarPage";
import FilesPage from "./pages/FilesPage";
import ContractsPage from "./pages/ContractsPage";
import PaymentsPage from "./pages/PaymentsPage";
import NotificationsPage from "./pages/NotificationsPage";
import SettingsPage from "./pages/SettingsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import ActivityPage from "./pages/ActivityPage";
import ClientsPage from "./pages/ClientsPage";
import InvoicesPage from "./pages/InvoicesPage";
import ProjectSectionPage from "./pages/ProjectSectionPage";
import AinaaInsightsPage from "./pages/AinaaInsightsPage";
import MessagesPage from "./pages/MessagesPage";
import AppLayout from "./components/layout/AppLayout";
import OnboardingFlow from "./components/OnboardingFlow";
import LoadingScreen from "./components/LoadingScreen";
import { Loader2 } from "lucide-react";
import { useState, useEffect } from "react";

function ProtectedRoute({ children }) {
  const { user, loading, updateUser } = useAuth();
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [showLoading, setShowLoading] = useState(true);

  useEffect(() => {
    if (!loading && user) {
      const timer = setTimeout(() => setShowLoading(false), 1600);
      return () => clearTimeout(timer);
    }
    if (!loading && !user) setShowLoading(false);
  }, [loading, user]);

  if (loading || (showLoading && user)) return <LoadingScreen />;

  if (!user) return <Navigate to="/login" replace />;

  const needsOnboarding = user.onboarding_completed === false && !onboardingDone;
  if (needsOnboarding) {
    return (
      <OnboardingFlow
        user={user}
        onComplete={() => {
          setOnboardingDone(true);
          if (updateUser) updateUser({ ...user, onboarding_completed: true });
        }}
      />
    );
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Navigate to="/?login=true" replace />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/shared/:token" element={<SharedProjectPage />} />
      <Route path="/showcase" element={<Navigate to="/" replace />} />

      {/* App — Top nav */}
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/connections" element={<ProtectedRoute><AppLayout><PeoplePage /></AppLayout></ProtectedRoute>} />
      <Route path="/activity" element={<ProtectedRoute><AppLayout><ActivityPage /></AppLayout></ProtectedRoute>} />

      {/* App — Projects */}
      <Route path="/projects" element={<ProtectedRoute><AppLayout><ProjectsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:id" element={<ProtectedRoute><AppLayout><ProjectDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:id/analytics" element={<ProtectedRoute><AppLayout><ProjectSectionPage section="analytics" /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:id/recents" element={<ProtectedRoute><AppLayout><ProjectSectionPage section="recents" /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:id/teams" element={<ProtectedRoute><AppLayout><ProjectSectionPage section="teams" /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:id/conversations" element={<ProtectedRoute><AppLayout><ProjectSectionPage section="conversations" /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:id/assets" element={<ProtectedRoute><AppLayout><ProjectSectionPage section="assets" /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:id/logistics" element={<ProtectedRoute><AppLayout><ProjectSectionPage section="logistics" /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:id/accounts" element={<ProtectedRoute><AppLayout><ProjectSectionPage section="accounts" /></AppLayout></ProtectedRoute>} />

      {/* App — Tools cluster */}
      <Route path="/invoices" element={<ProtectedRoute><AppLayout><InvoicesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute><AppLayout><ContractsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/files" element={<ProtectedRoute><AppLayout><FilesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><AppLayout><PaymentsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><AppLayout><ClientsPage /></AppLayout></ProtectedRoute>} />

      {/* App — Personal */}
      <Route path="/calendar" element={<ProtectedRoute><AppLayout><CalendarPage /></AppLayout></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><AppLayout><NotificationsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AppLayout><AnalyticsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/spectre" element={<ProtectedRoute><AppLayout><SpecterPage /></AppLayout></ProtectedRoute>} />
      <Route path="/ainaa" element={<ProtectedRoute><AppLayout><AinaaInsightsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><AppLayout><MessagesPage /></AppLayout></ProtectedRoute>} />

      {/* Redirects for old routes */}
      <Route path="/people" element={<Navigate to="/connections" replace />} />
      <Route path="/introw" element={<Navigate to="/connections" replace />} />
      <Route path="/ai-tools" element={<Navigate to="/spectre" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
